import React, { useState, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, TextField, Button, Alert, CircularProgress,
} from '@mui/material';
import { SearchField } from '../Checkout';
import { validateClaimForm } from '../../claim-helpers-shared';

const ClaimCreateForm = ({ posConfig }) => {
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        kdnr: '',
        kdnrLabel: '',
        absender: '',
        sendungsnummer: '',
        packstuecknummer: '',
        trackingnummer: '',
        id_nummer: '',
        kommentar: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWebsocketEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleWebsocketEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);
            if (msg.task === 'respCreateClaimInternal') {
                setSubmitting(false);
                if (msg.status === 'succeeded') {
                    setSuccess(`Suchanfrage erstellt: ${msg.claimId}`);
                    setError(null);
                    setTimeout(() => navigate('..'), 1500);
                } else {
                    setError(msg.message || 'Fehler beim Erstellen.');
                }
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleKdnrSelect = (kdnr) => {
        setFormData((prev) => ({ ...prev, kdnr: String(kdnr) }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!formData.kdnr.trim()) {
            setError('Bitte einen Kunden auswählen.');
            return;
        }

        const validationError = validateClaimForm(formData);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);

        const payload = {
            task: 'createClaimInternal',
            station: cookies.station,
            staff: cookies.staff,
            kdnr: Number(formData.kdnr.trim()),
            absender: formData.absender.trim(),
            sendungsnummer: formData.sendungsnummer.trim(),
        };

        if (formData.packstuecknummer.trim()) payload.packstuecknummer = formData.packstuecknummer.trim();
        if (formData.trackingnummer.trim()) payload.trackingnummer = formData.trackingnummer.trim();
        if (formData.id_nummer.trim()) payload.id_nummer = formData.id_nummer.trim();
        if (formData.kommentar.trim()) payload.kommentar = formData.kommentar.trim();

        sendMessage(JSON.stringify(payload));
    };

    const isConnected = readyState === ReadyState.OPEN;

    return (
        <Box sx={{ p: 2, maxWidth: 600 }}>
            <Card>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Suchanfrage erstellen
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                        {/* Kundensuche via SearchField aus Checkout */}
                        <SearchField
                            posConfig={posConfig}
                            onSelect={handleKdnrSelect}
                            PaperComponent={Box}
                            paperSx={{ p: 0 }}
                            noAutofocus
                        />
                        {formData.kdnr && (
                            <Alert severity="info" sx={{ py: 0 }}>
                                Ausgewählter Kunde: <strong>{formData.kdnr}</strong>
                            </Alert>
                        )}

                        <TextField label="Absender" name="absender" value={formData.absender}
                            onChange={handleChange} required size="small" />
                        <TextField label="Sendungsnummer" name="sendungsnummer" value={formData.sendungsnummer}
                            onChange={handleChange} required size="small" />
                        <TextField label="Packstücknummer" name="packstuecknummer" value={formData.packstuecknummer}
                            onChange={handleChange} size="small" />
                        <TextField label="Trackingnummer" name="trackingnummer" value={formData.trackingnummer}
                            onChange={handleChange} size="small" />
                        <TextField label="ID-Nummer" name="id_nummer" value={formData.id_nummer}
                            onChange={handleChange} size="small" />
                        <TextField label="Kommentar" name="kommentar" value={formData.kommentar}
                            onChange={handleChange} multiline rows={3} size="small" />

                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Button type="submit" variant="contained" disabled={submitting || !isConnected}>
                                {submitting ? <CircularProgress size={24} /> : 'Suchanfrage erstellen'}
                            </Button>
                            <Button variant="outlined" onClick={() => navigate('..')} disabled={submitting}>
                                Abbrechen
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ClaimCreateForm;
